import { useEffect, useState } from 'react';
import { Calculator, Download } from 'lucide-react';
import { useOrg } from '../lib/org';
import { calculateVat201, vatPeriodRange, type Vat201 } from '../lib/vat';
import { formatDate, formatMoney } from '../lib/format';

export default function VAT201() {
  const { currentOrg } = useOrg();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [result, setResult] = useState<Vat201 | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!currentOrg) return;
    const r = vatPeriodRange(new Date(), currentOrg.vat_period);
    setFrom(r.from);
    setTo(r.to);
  }, [currentOrg]);

  async function recalc() {
    if (!currentOrg || !from || !to) return;
    setBusy(true); setErr(null); setResult(null);
    const out = await calculateVat201(currentOrg.id, from, to);
    setBusy(false);
    if ('error' in out) setErr(out.error);
    else setResult(out);
  }

  useEffect(() => { if (from && to && currentOrg) recalc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [from, to, currentOrg]);

  function downloadPdf() {
    if (!result) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(renderVat201Html(result, currentOrg!.name, currentOrg!.vat_number ?? ''));
    w.document.close();
    setTimeout(() => w.print(), 250);
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="h-page">VAT201</h1>
          <p className="text-text-dim text-sm mt-1">SARS VAT return — calculated from your posted journals.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={recalc} disabled={busy} className="btn btn-secondary disabled:opacity-50"><Calculator size={14} /> Recalculate</button>
          <button onClick={downloadPdf} disabled={!result} className="btn btn-primary disabled:opacity-50"><Download size={14} /> Print / save PDF</button>
        </div>
      </header>

      {currentOrg && !currentOrg.is_vat_registered && (
        <div className="card border-gold/30 bg-gold/5">
          <p className="text-sm text-gold">
            Heads up — this org is set to <strong>not VAT-registered</strong>. The VAT201 will compute, but you don't need to file unless you cross the R1M turnover threshold or voluntarily register.
            Toggle this in Settings.
          </p>
        </div>
      )}

      <div className="card grid grid-cols-3 gap-4">
        <div className="field"><label>Period from</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div className="field"><label>Period to</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <div className="field">
          <label>Quick periods</label>
          <select onChange={(e) => {
            if (!currentOrg) return;
            const choice = e.target.value;
            const now = new Date();
            let d = now;
            if (choice === 'prev') {
              d = new Date(now.getFullYear(), now.getMonth() - 2, 1);
            } else if (choice === 'current') {
              d = now;
            } else return;
            const r = vatPeriodRange(d, currentOrg.vat_period);
            setFrom(r.from); setTo(r.to);
          }}>
            <option value="">— choose —</option>
            <option value="current">Current period</option>
            <option value="prev">Previous period</option>
          </select>
        </div>
      </div>

      {err && <div className="text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}

      {result && (
        <div className="grid grid-cols-2 gap-4">
          <div className="card">
            <h2 className="h-section mb-3">Output VAT <span className="text-xs font-mono text-text-mute uppercase tracking-widest ml-2">sales side</span></h2>
            <BoxRow label="Box 1 · Standard-rated supplies (excl. VAT)" amount={result.box_1_net} />
            <BoxRow label="Box 1A · Output VAT (15%)" amount={result.box_1a_vat} />
            <BoxRow label="Box 2 · Zero-rated supplies" amount={result.box_2_net} muted />
            <BoxRow label="Box 3 · Exempt + non-supplies" amount={result.box_3_net} muted />
            <div className="border-t border-line mt-2 pt-2 flex justify-between text-sm">
              <span className="font-serif font-semibold">Total output VAT</span>
              <span className="num text-emerald">{formatMoney(result.output_vat_total)}</span>
            </div>
          </div>

          <div className="card">
            <h2 className="h-section mb-3">Input VAT <span className="text-xs font-mono text-text-mute uppercase tracking-widest ml-2">purchases side</span></h2>
            <BoxRow label="Box 14 · Standard-rated purchases (excl. VAT)" amount={result.box_14_net} />
            <BoxRow label="Box 14A · Input VAT (15%)" amount={result.box_14a_vat} />
            <div className="border-t border-line mt-2 pt-2 flex justify-between text-sm">
              <span className="font-serif font-semibold">Total input VAT</span>
              <span className="num text-emerald">{formatMoney(result.input_vat_total)}</span>
            </div>
          </div>

          <div className="card col-span-2 border-emerald/30 bg-emerald/5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="h-section">Box 18 · Net VAT {result.is_refund ? 'refundable' : 'payable to SARS'}</h2>
                <p className="text-xs text-text-mute font-mono mt-1">
                  Period {formatDate(result.period_from)} → {formatDate(result.period_to)}
                </p>
              </div>
              <div className={`font-serif text-3xl font-semibold num ${result.is_refund ? 'text-gold' : 'text-emerald'}`}>
                {result.is_refund ? '+' : ''}{formatMoney(result.box_18_payable)}
              </div>
            </div>
          </div>

          <div className="card col-span-2">
            <h3 className="text-sm font-semibold mb-2">How this is computed</h3>
            <ul className="text-xs text-text-dim space-y-1 list-disc pl-5">
              <li>We sum the <span className="font-mono text-text">debit</span>/<span className="font-mono text-text">credit</span> on every posted journal line in the period, joined to its tax code and account.</li>
              <li>Revenue lines (4xxx) tagged with a 15% output code count toward Box 1; their VAT amount lands in Box 1A.</li>
              <li>Expense / COGS / asset lines tagged with a 15% input code count toward Box 14; their VAT lands in Box 14A.</li>
              <li>Zero-rated (ZER) and exempt (EXM) supplies are reported but don't produce VAT.</li>
              <li>The VAT control accounts themselves (sars_role <span className="font-mono">vat_input</span> / <span className="font-mono">vat_output</span>) are excluded from the net totals.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function BoxRow({ label, amount, muted }: { label: string; amount: number; muted?: boolean }) {
  return (
    <div className={'flex justify-between py-1.5 text-sm ' + (muted ? 'text-text-mute' : 'text-text-dim')}>
      <span>{label}</span>
      <span className={'num ' + (muted ? '' : 'text-text')}>{formatMoney(amount)}</span>
    </div>
  );
}

function renderVat201Html(v: Vat201, orgName: string, vatNumber: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"/><title>VAT201 — ${orgName}</title>
  <style>
    body { font-family: Georgia, serif; padding: 36pt; color: #0f1521; }
    h1 { font-size: 22pt; margin: 0 0 4pt; }
    .meta { font-size: 10pt; color: #555; margin-bottom: 24pt; }
    table { width: 100%; border-collapse: collapse; margin-top: 16pt; }
    td, th { padding: 6pt 8pt; border-bottom: 1px solid #ddd; font-size: 11pt; }
    th { background: #f5efe0; text-align: left; }
    .right { text-align: right; font-family: 'Courier New', monospace; }
    .total { font-weight: bold; font-size: 13pt; }
    .stamp { margin-top: 24pt; padding: 12pt; background: #f5efe0; border: 2px solid #0c8f60; }
  </style></head><body>
  <h1>VAT201 — ${escapeHtml(orgName)}</h1>
  <div class="meta">VAT no. ${escapeHtml(vatNumber || '—')} · Period ${v.period_from} to ${v.period_to}</div>
  <table>
    <thead><tr><th>Box</th><th>Description</th><th class="right">Amount (R)</th></tr></thead>
    <tbody>
      <tr><td>1</td><td>Standard-rated supplies (excl. VAT)</td><td class="right">${money(v.box_1_net)}</td></tr>
      <tr><td>1A</td><td>VAT charged on Box 1 (15%)</td><td class="right">${money(v.box_1a_vat)}</td></tr>
      <tr><td>2</td><td>Zero-rated supplies</td><td class="right">${money(v.box_2_net)}</td></tr>
      <tr><td>3</td><td>Exempt and non-supplies</td><td class="right">${money(v.box_3_net)}</td></tr>
      <tr><td colspan="2" class="total">Total output VAT</td><td class="right total">${money(v.output_vat_total)}</td></tr>
      <tr><td>14</td><td>Standard-rated purchases (excl. VAT)</td><td class="right">${money(v.box_14_net)}</td></tr>
      <tr><td>14A</td><td>VAT on Box 14 (15%)</td><td class="right">${money(v.box_14a_vat)}</td></tr>
      <tr><td colspan="2" class="total">Total input VAT</td><td class="right total">${money(v.input_vat_total)}</td></tr>
    </tbody>
  </table>
  <div class="stamp">
    <strong>Box 18 — ${v.is_refund ? 'Net VAT refundable' : 'Net VAT payable to SARS'}: R ${money(v.box_18_payable)}</strong>
  </div>
  <p style="font-size:9pt;color:#888;margin-top:24pt">
    Generated by DodoBooks. This is a preview of the VAT201 fields — direct eFiling submission requires SARS ISV registration (pending).
  </p>
  </body></html>`;
}

function money(n: number) { return n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function escapeHtml(s: string) { return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string)); }
